/**
 * @engine/vfx - Point Map Asset Types
 * 
 * Point Map asset definition and serialization.
 * A Point Map bakes an image into position/color data for VFX particle systems.
 */

/**
 * Point Map asset definition (stored as .pointmap file).
 */
export interface PointMapDefinition {
  /** Version for forward compatibility */
  version: 1;
  
  /** Source image asset GUID */
  sourceImageGuid: string;
  
  /** Alpha threshold (0-255) for considering a pixel valid */
  alphaThreshold: number;
  
  /** Position scale factor */
  positionScale: number;
  
  /** Center positions around origin */
  centerOrigin: boolean;
  
  /** Shuffle points for random distribution */
  shuffle: boolean;
}

/**
 * Compiled Point Map result (stored in cache).
 * The actual data arrays are stored separately as binary.
 */
export interface CompiledPointMapMeta {
  /** Number of valid points */
  pointCount: number;
  
  /** Texture dimensions */
  width: number;
  height: number;
  
  /** Source image GUID for cache invalidation */
  sourceImageGuid: string;
  
  /** Settings hash for cache invalidation */
  settingsHash: string;
  
  /** Compilation timestamp */
  compiledAt: number;
}

/**
 * Runtime Point Map data (loaded from cache).
 */
export interface PointMapData {
  /** Metadata */
  meta: CompiledPointMapMeta;
  
  /** Position data (Float32Array: xyzw per point, w=1 for valid) */
  positionData: Float32Array;
  
  /** Color data (Uint8ClampedArray: rgba per point) */
  colorData: Uint8ClampedArray;
}

/**
 * Create default Point Map definition.
 */
export function createDefaultPointMapDefinition(sourceImageGuid: string = ''): PointMapDefinition {
  return {
    version: 1,
    sourceImageGuid,
    alphaThreshold: 1,
    positionScale: 1,
    centerOrigin: true,
    shuffle: true,
  };
}

/**
 * Create a settings hash for cache invalidation.
 */
export function createSettingsHash(def: PointMapDefinition): string {
  return `${def.alphaThreshold}-${def.positionScale}-${def.centerOrigin}-${def.shuffle}`;
}

/**
 * Serialize Point Map definition to JSON string.
 */
export function serializePointMap(def: PointMapDefinition): string {
  return JSON.stringify(def, null, 2);
}

/**
 * Deserialize Point Map definition from JSON string.
 */
export function deserializePointMap(json: string): PointMapDefinition {
  const data = JSON.parse(json);
  
  // Validate version
  if (data.version !== 1) {
    throw new Error(`Unsupported Point Map version: ${data.version}`);
  }
  
  return {
    version: 1,
    sourceImageGuid: data.sourceImageGuid ?? '',
    alphaThreshold: data.alphaThreshold ?? 1,
    positionScale: data.positionScale ?? 1,
    centerOrigin: data.centerOrigin ?? true,
    shuffle: data.shuffle ?? true,
  };
}
